"use client";

import {
  SlopeChart,
  type SlopeSeries,
} from "@/components/charts/SlopeChart";
import { getPalette } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";

import type { EmissionsSummaryRow } from "@/app/climate/_components/ClimateEmissionsChart";

export type EmissionsSlopeProps = {
  emissions: EmissionsSummaryRow[];
  /** First year of the comparison window (currently 2005). */
  populationStartYear: number;
  populationStartValue: number;
  /**
   * Last year of the comparison window. Must match the year of the
   * latest emissions row in `emissions` so the chart's population
   * series and emissions series share the same time window.
   */
  populationEndYear: number;
  populationEndValue: number;
};

export function ClimateEmissionsSlope({
  emissions,
  populationStartYear,
  populationStartValue,
  populationEndYear,
  populationEndValue,
}: EmissionsSlopeProps) {
  const p = getPalette(usePrefersDark());
  const popPct =
    populationStartValue === 0
      ? 0
      : ((populationEndValue - populationStartValue) / populationStartValue) *
        100;

  const community = emissions.find(
    (r) => r.metric.toLowerCase().includes("community") && !r.metric.toLowerCase().includes("per capita"),
  );
  const perCapita = emissions.find((r) =>
    r.metric.toLowerCase().includes("per capita"),
  );
  const corporate = emissions.find((r) => r.scope === "corporate");

  const series: SlopeSeries[] = [
    {
      key: "population",
      label: "Population",
      color: p.slate,
      start: 0,
      end: popPct,
    },
    {
      key: "community_total",
      label: "Community emissions (total)",
      color: p.teal,
      start: 0,
      end: community?.change_pct ?? 0,
    },
    {
      key: "corporate",
      label: "City operations emissions",
      color: p.moss,
      start: 0,
      end: corporate?.change_pct ?? 0,
    },
  ];

  return (
    <SlopeChart
      series={series}
      height={420}
      startLabel={String(populationStartYear)}
      endLabel={String(populationEndYear)}
      yTickFormatter={(v) => `${v > 0 ? "+" : ""}${v}%`}
      endValueFormatter={(v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}%`}
      rightAnnotation={
        perCapita
          ? {
              fromSeriesKey: "population",
              toSeriesKey: "community_total",
              label: `${perCapita.change_pct.toFixed(1)}%`,
              sublabel: "per-capita drop",
              color: p.signal,
            }
          : undefined
      }
    />
  );
}
